import Vue from 'vue';
import Vuex from 'vuex';
import AppSession from './AppSession';
import {CasePage, ValueChangeOperator} from '@/model/CommonModel';

Vue.use(Vuex);
export const ComponentEventBus: Vue = new Vue();
export const SessionTimerEventBus: Vue = new Vue();
export const CaseActionEventBus: Vue = new Vue();
export const CaseNavigatorEventBus: Vue = new Vue();
export enum CasePageType {
    NORMAL = '1',       // Case Repeat Page가 아님 (일반 뷰)
    REPEAT = '2',       // 저장 된 Case Repeat Page
    REPEAT_NEW = '3', // Add를 통해 새롭게 추가 된 Case Repeat Page
}

const initialState = {
    userKey: undefined,
    userId: undefined,
    userName: undefined,
    userRoleName: undefined,
    token: undefined,
    menus: [],
    param: {},
    privileges: [],
    privilegeMap: {},
    valueChangedMap: {},
    nullFlavorChangedMap: {},
    notationChangedMap: {},
    casePageMap: new Map(),
    fullTextSupported: false,       /* full text 기능 활성화 여부 */
    fullTextState: true,            /* full text option flag */
    clientType: undefined,
    organization: undefined,
    unblind: false,
    senderName: undefined,
    environment: undefined,
    caseNo: '',
    sessionExtendRejected: false,   // 세션 만료를 하지 않고 거부하였을 경우 플래그
    mainContentLoading: false, // 대 메뉴간 이동 시 사용하는 Loading
    caseStatusLoaded: false,
    caseFormDisabled: false,
    formStatus: {
        locked: false,
        reviewed: false,
        deleted: false,
        caseWorkflowStatus: 'SAVED',
        caseWorkflowLabel: '',
    },
    useWorkflow: false,
    scrollTop: -1,
    isAddedCaseRepeatPage: CasePageType.NORMAL,
    timeZone: '',
    dateError: false,
    actionButtonDowned: false,
    moreButtonVisible: false,
    isProgressCaseNRAiCoding: false,
    visibleDiscardConfirmDialog: false
};

export default new Vuex.Store(
    {
        state: {
            ...initialState
        },
        mutations: {
            setSession(state, session) {
                state.userKey = session.userKey;
                state.userId = session.userId;
                state.token = session.token;
                state.sessionExtendRejected = false;
                AppSession.setSession(session);
            },
            setUserId(state, userId) {
                if (state.userId === userId) {
                    return;
                }

                state.userId = userId;
            },
            setUserName(state, userName) {
                if (state.userName === userName) {
                    return;
                }
                state.userName = userName;
            },
            setRoleName(state, userRoleName) {
                if (state.userRoleName === userRoleName) {
                    return;
                }
                state.userRoleName = userRoleName;
            },
            setMenus(state, menus) {
                state.menus = menus;
            },
            setPrivileges(state, privileges) {
                state.privileges = privileges;
                const newPrivilegeMap: any = {};
                privileges.forEach((privilege: any) => {
                    newPrivilegeMap[privilege.privId] = true;
                });

                state.privilegeMap = newPrivilegeMap;
            },
            setLanguage(state, language) {
                ComponentEventBus.$emit('languageChanged', language);
            },
            setAppendix(state, appendix) {
                ComponentEventBus.$emit('appendixChanged', appendix);
            },
            setClientType(state, clientType) {
                if (state.clientType === clientType) {
                    return;
                }
                state.clientType = clientType;
            },
            setOrganization(state, organization) {
                if (state.organization === organization) {
                    return;
                }
                state.organization = organization;
            },
            setUnblind(state, unblind) {
                if (state.unblind === unblind) {
                    return;
                }
                state.unblind = unblind;
            },
            setSenderName(state, senderName) {
                if (state.senderName === senderName) {
                    return;
                }
                state.senderName = senderName;
            },
            setEnvironment(state, environment) {
                if (state.environment === environment) {
                    return;
                }
                state.environment = environment;
            },
            setCaseNo(state, caseNo) {
                if (state.caseNo === caseNo) {
                    return;
                }
                state.caseNo = caseNo;
            },
            setMoreButtonVisible(state, visible) {
                state.moreButtonVisible = visible;
            },
            initChangeMap(state) {
                state.valueChangedMap = {};
                state.nullFlavorChangedMap = {};
                state.notationChangedMap = {};
            },
            commitValue(state, operator: ValueChangeOperator) {
                if (!operator.itemId) {
                    return;
                }

                const valueChangedMap: any = state.valueChangedMap;
                const value = operator.value ? operator.value.toString() : '';
                const originValue = operator.originValue ? operator.originValue.toString() : '';
                if (value === originValue) {
                    delete valueChangedMap[operator.itemId];
                } else {
                    valueChangedMap[operator.itemId] = operator.value;
                }

                state.valueChangedMap = {...valueChangedMap};
            },
            removeChildren(state, storeId: string) {
                const valueChangedMap: any = state.valueChangedMap;
                (Object.keys(valueChangedMap) ?? [])
                    .filter((key: string) => key.indexOf(storeId) >= 0)
                    .forEach((key: string) => {
                        delete valueChangedMap[key];
                    });

                state.valueChangedMap = {...valueChangedMap};

                const nullFlavorChangedMap: any = state.nullFlavorChangedMap;
                (Object.keys(nullFlavorChangedMap) ?? [])
                    .filter((key: string) => key.indexOf(storeId) >= 0)
                    .forEach((key: string) => {
                        delete nullFlavorChangedMap[key];
                    });

                state.nullFlavorChangedMap = {...nullFlavorChangedMap};

                const notationChangedMap: any = state.notationChangedMap;
                (Object.keys(notationChangedMap) ?? [])
                    .filter((key: string) => key.indexOf(storeId) >= 0)
                    .forEach((key: string) => {
                        delete notationChangedMap[key];
                    });

                state.notationChangedMap = {...notationChangedMap};
            },
            commitNullFlavor(state, operator: ValueChangeOperator) {
                if (!operator.itemId) {
                    return;
                }
                const nullFlavorChangedMap: any = state.nullFlavorChangedMap;
                const value = operator.value ? operator.value.toString() : '';
                const originValue = operator.originValue ? operator.originValue.toString() : '';
                if (value === originValue) {
                    delete nullFlavorChangedMap[operator.itemId];
                } else {
                    nullFlavorChangedMap[operator.itemId] = operator.value;
                }

                state.nullFlavorChangedMap = {...nullFlavorChangedMap};
            },
            commitNotation(state, operator: ValueChangeOperator) {
                if (!operator.itemId) {
                    return;
                }
                const notationChangedMap: any = state.notationChangedMap;
                const value = operator.value ? operator.value.toString() : '';
                const originValue = operator.originValue ? operator.originValue.toString() : '';
                if (value === originValue) {
                    delete notationChangedMap[operator.itemId];
                } else {
                    notationChangedMap[operator.itemId] = operator.value;
                }

                state.notationChangedMap = {...notationChangedMap};
            },
            extendSessionTime(state) {
                SessionTimerEventBus.$emit('extendSessionTime');
            },
            rejectSessionExtension(state) {
                state.sessionExtendRejected = true;
            },
            clear(state) {
                state = {...initialState};
            },
            setParam(state, param: any) {
                state.param = param;
            },
            mainContentLoading(state) {
                state.mainContentLoading = true;
            },
            mainContentLoadingEnd(state) {
                state.mainContentLoading = false;
            },
            setFormStatus(state, status) {
                state.formStatus = {...status};
            },
            initFormStatus(state) {
                state.formStatus = {
                    ...initialState.formStatus
                };
            },
            setFormStatusLoaded(state, loaded: boolean) {
                state.caseStatusLoaded = loaded;
            },
            setCaseFormDisabled(state, disabled: boolean) {
                state.caseFormDisabled = disabled;
            },
            setCasePages(state, casePages: CasePage[]) {
                const casePageMap: Map<string, CasePage> = new Map();
                casePages.forEach((page: CasePage) => {
                    casePageMap.set(page.pageId, page);
                });

                state.casePageMap = casePageMap;
            },
            initScrollTop(state, scrollTop: number) {
                state.scrollTop = -1;
            },
            setScrollTop(state, scrollTop: number) {
                state.scrollTop = scrollTop;
            },
            offFullTextSupport(state) {
                state.fullTextSupported = false;
            },
            onFullTextSupport(state) {
                state.fullTextSupported = true;
            },
            setFullText(state, value: boolean) {
                state.fullTextState = value;
            },
            toggleFullText(state) {
                state.fullTextState = !state.fullTextState;
            },
            setIsAddedCaseRepeatPage(state, newPage: boolean) {
                state.isAddedCaseRepeatPage = newPage ? CasePageType.REPEAT_NEW : CasePageType.REPEAT;
            },
            setIsNormalCaseRepeatPage(state) {
                state.isAddedCaseRepeatPage = CasePageType.NORMAL;
            },
            useWorkflow(state, status: boolean) {
                state.useWorkflow = status;
            },
            setTimeZone(state, status: string) {
                state.timeZone = status;
            },
            setDateError(state, status: boolean) {
                state.dateError = status;
            },
            setActionButtonDown(state, status: boolean) {
                state.actionButtonDowned = status;
            },
            setStartCaseNRAiCoding(state, status: boolean) {
                state.isProgressCaseNRAiCoding = status;
            },
            setVisibleDiscardConfirmDialog(state, visible: boolean) {
                state.visibleDiscardConfirmDialog = visible;
            }

        },
        actions: {},
        getters: {
            userKey: state => {
                return state.userKey === null || state.userKey === undefined
                    ? AppSession.getUserKey()
                    : state.userKey;
            },
            userId: state => state.userId,
            userName: state => state.userName,
            userRoleName: state => state.userRoleName,
            menus: state => state.menus,
            param: state => state.param,
            privileges: state => state.privileges,
            privilegeMap: state => state.privilegeMap,
            clientType: state => state.clientType,
            organization: state => state.organization,
            unblind: state => state.unblind,
            senderName: state => state.senderName,
            environment: state => state.environment,
            isServiceEnvironment: state => state.environment === 'cubeSAFETY-R3',
            caseNo: state => state.caseNo,
            isSponsorType: state => state.clientType === 'SPO',
            isCroType: state => state.clientType === 'CRO',
            mainContentLoading: state => state.mainContentLoading,
            changedMap: state => {
                return Object.assign({}, state.valueChangedMap, state.notationChangedMap, state.nullFlavorChangedMap);
            },
            hasChanged: state => {
                const changed = Object.assign({}, state.valueChangedMap, state.notationChangedMap, state.nullFlavorChangedMap);
                return Object.keys(changed).length > 0;
            },
            isCaseStatusLoaded: state => state.caseStatusLoaded,
            isCaseLocked: state => state.formStatus.locked,
            isCaseReviewed: state => state.formStatus.reviewed,
            isFormDeleted: state => state.formStatus.deleted,
            caseWorkflowStatus: state => state.formStatus.caseWorkflowStatus,
            isSavedWorkflow: state => state.formStatus.caseWorkflowStatus === 'SAVED',
            caseWorkflowLabel: state => state.formStatus.caseWorkflowLabel,
            isUseWorkflow: state => state.useWorkflow,
            isCaseFormDisabled: state => state.caseFormDisabled,
            casePageMap: state => state.casePageMap,
            scrollTop: state => state.scrollTop,
            fullTextSupported: state => state.fullTextSupported,
            fullTextActivated: state => {
                return !state.fullTextSupported || state.fullTextState;
            },
            isAddedCaseRepeatPage: state => state.isAddedCaseRepeatPage === CasePageType.REPEAT_NEW,
            isCaseRepeatPageView: state => state.isAddedCaseRepeatPage !== CasePageType.NORMAL,
            getTimeZone: state => state.timeZone,
            hasDateError: state => state.dateError,
            isActionButtonDowned: state => state.actionButtonDowned,
            isMoreButtonVisible: state => state.moreButtonVisible,
            isProgressCaseNRAiCoding: state => state.isProgressCaseNRAiCoding,
            visibleDiscardConfirmDialog: state => state.visibleDiscardConfirmDialog,
        },
        modules: {}
    });
